import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import {
  FIELD_LABELS,
  LEAD_FIELD_LABELS,
  LEAD_QUALIFICATION_FIELDS,
  QUALIFICATION_LABELS,
  leadPushBlockers,
  suggestDealName,
  type ContactFormField,
  type LeadField,
} from '@wr/shared';
import { Icon } from '../../components/Icon.js';
import { useToast } from '../../components/Toast.js';
import {
  Checkbox,
  Dialog,
  Field,
  ManyOfChips,
  Notice,
  RadioChips,
  SelectField,
  TextAreaField,
} from '../../components/ui.js';
import { LISTS, contactPushBlockers, marketLabel, validateContact } from '../../lists.js';
import { useOffline } from '../../offline/context.js';
import { newId } from '../../offline/ids.js';
import type { LocalRecord } from '../../offline/store.js';
import { useSignedIn } from '../../session.js';
import { fullName } from '../people/data.js';
import { loadOwners, type LeadSheetData, type Owner } from './data.js';
import { stageFor, useStages } from './stages.js';

const str = (v: unknown) => (typeof v === 'string' ? v : '');

const QUALIFICATIONS = [
  { value: 'qualified', label: QUALIFICATION_LABELS.qualified },
  { value: 'disqualified', label: `${QUALIFICATION_LABELS.disqualified}, Nurture` },
];

/** What the short form shows for a lead: what blocks it, as answered so far. */
function leadFieldsShown(lead: LocalRecord, draft: Record<string, unknown>, first: LeadField[]) {
  const merged = { ...lead, ...draft };
  const now = leadPushBlockers(merged).map((b) => b.field);
  const shown = [...new Set([...first, ...now])];
  return shown.filter((f) =>
    merged.qualification === 'disqualified'
      ? f !== 'product_fit'
      : f !== 'disqualified_why' && f !== 'disqualified_when',
  );
}

/**
 * To do by keyboard (brief section 6.9): a short form with only what blocks
 * the lead's push. "Not booked yet" is a valid answer for the Project Call:
 * it becomes a HubSpot task. Saved on the device first.
 */
export function CompleteLead({ data, onDone }: { data: LeadSheetData; onDone(): void }) {
  const { user, can } = useSignedIn();
  const { save } = useOffline();
  const toast = useToast();
  const lead = data.lead;
  const qualify = can('leads.qualify');
  const stages = useStages();
  const [first] = useState<LeadField[]>(() => leadPushBlockers(lead).map((b) => b.field));
  const [draft, setDraft] = useState<Record<string, unknown>>(() => {
    const d: Record<string, unknown> = {};
    if (first.includes('deal_name')) {
      d.deal_name = suggestDealName(str(lead.company_name), data.eventCode);
    }
    if (first.includes('deal_market') && Array.isArray(lead.applications)) {
      d.deal_market = (lead.applications as string[])[0] ?? '';
    }
    if (first.includes('owner_user_id')) d.owner_user_id = user.id;
    return d;
  });
  const [notBooked, setNotBooked] = useState(false);
  const [owners, setOwners] = useState<Owner[] | null>(null);
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);

  useEffect(() => {
    let current = true;
    void loadOwners()
      .catch(() => null)
      .then((list) => current && setOwners(list));
    return () => {
      current = false;
    };
  }, []);

  const merged = { ...lead, ...draft };
  const shown = leadFieldsShown(lead, draft, first).filter(
    (f) => qualify || !(LEAD_QUALIFICATION_FIELDS as readonly string[]).includes(f),
  );
  const askCall = qualify && merged.qualification === 'qualified' && !str(lead.project_call_date);
  const set = (field: string, value: unknown) =>
    setDraft((d) => {
      const next = { ...d, [field]: value };
      if (field === 'qualification' && !str(lead.stage_id)) {
        next.stage_id = stageFor(value, stages) ?? '';
      }
      return next;
    });
  const text = (field: LeadField) => str(merged[field]);

  async function submit() {
    if (saving) return;
    const fields: Record<string, unknown> = {};
    for (const [field, value] of Object.entries(draft)) {
      const clean = typeof value === 'string' ? value.trim() : value;
      if (clean === '' || (Array.isArray(clean) && clean.length === 0)) continue;
      if (JSON.stringify(clean) !== JSON.stringify(lead[field] ?? null)) fields[field] = clean;
    }
    if (!askCall || notBooked) delete fields.project_call_date;
    if (Object.keys(fields).length === 0) return onDone();
    setSaving(true);
    try {
      await save({
        opId: newId(),
        entity: 'lead',
        id: lead.id,
        action: 'upsert',
        changedAt: new Date().toISOString(),
        fields,
      });
      const left = leadPushBlockers({ ...lead, ...fields });
      toast(
        left.length === 0
          ? 'Documented. It goes to HubSpot by itself.'
          : 'Saved, a few things are still missing.',
      );
      onDone();
    } catch {
      setFailed('This device could not save. Nothing was lost: try again.');
      setSaving(false);
    }
  }

  const countries = (field: LeadField, same?: string) => (
    <SelectField
      key={field}
      label={LEAD_FIELD_LABELS[field]}
      value={text(field)}
      onChange={(e) => set(field, e.target.value)}
    >
      <option value="">Select</option>
      {same && <option value={same}>Same as the company ({same})</option>}
      {LISTS.countries.map((c) => (
        <option key={c} value={c}>
          {c}
        </option>
      ))}
    </SelectField>
  );

  const field = (f: LeadField) => {
    switch (f) {
      case 'role':
        return (
          <RadioChips
            key={f}
            label="Role in the project"
            options={LISTS.roleOptions}
            value={text(f)}
            onChange={(v) => set(f, v)}
          />
        );
      case 'qualification':
        return (
          <RadioChips
            key={f}
            label="Outcome"
            options={QUALIFICATIONS}
            value={text(f)}
            onChange={(v) => set(f, v)}
          />
        );
      case 'applications':
        return (
          <ManyOfChips
            key={f}
            label="Operating market(s)"
            options={LISTS.markets.map((m) => ({ value: m.code, label: marketLabel(m.code) }))}
            value={Array.isArray(merged.applications) ? (merged.applications as string[]) : []}
            onChange={(v) => set(f, v)}
          />
        );
      case 'deal_market':
        return (
          <RadioChips
            key={f}
            label="Deal market"
            options={LISTS.markets.map((m) => ({ value: m.code, label: m.code }))}
            value={text(f)}
            onChange={(v) => set(f, v)}
          />
        );
      case 'country':
        return countries(f);
      case 'installation_country':
        return countries(f, str(merged.country) || undefined);
      case 'product_fit':
        return (
          <TextAreaField
            key={f}
            label="Product fit"
            rows={3}
            value={text(f)}
            onChange={(e) => set(f, e.target.value)}
          />
        );
      case 'owner_user_id':
        return (
          <SelectField
            key={f}
            label="Deal owner"
            value={text(f)}
            onChange={(e) => set(f, e.target.value)}
          >
            {(owners ?? [{ id: user.id, name: `${user.firstName} ${user.lastName}` }]).map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </SelectField>
        );
      case 'stage_id':
        return (
          <SelectField
            key={f}
            label="Deal stage"
            value={text(f)}
            onChange={(e) => set(f, e.target.value)}
          >
            <option value="">Select</option>
            {stages.map((s) => (
              <option key={s.id} value={s.id}>
                {s.label}
              </option>
            ))}
          </SelectField>
        );
      default:
        return (
          <Field
            key={f}
            label={LEAD_FIELD_LABELS[f]}
            placeholder={f === 'disqualified_when' ? 'e.g. new line planned in 2028' : undefined}
            value={text(f)}
            onChange={(e) => set(f, e.target.value)}
          />
        );
    }
  };

  const name = str(lead.company_name) || str(lead.deal_name) || 'the lead';
  return (
    <Dialog
      title={`Complete ${name}`}
      onClose={onDone}
      footer={
        <>
          <button type="button" className="btn" onClick={onDone}>
            Cancel
          </button>
          <ByVoiceInstead kind="lead" id={lead.id} />
          <button
            type="button"
            className="btn primary"
            disabled={saving}
            onClick={() => void submit()}
          >
            Save
          </button>
        </>
      }
    >
      {shown.length === 0 && !askCall ? <p>Nothing missing.</p> : shown.map(field)}
      {askCall && (
        <>
          <Field
            label="Project Call"
            type="date"
            disabled={notBooked}
            value={text('project_call_date')}
            onChange={(e) => set('project_call_date', e.target.value)}
          />
          <Checkbox checked={notBooked} onChange={(e) => setNotBooked(e.target.checked)}>
            Not booked yet (becomes a HubSpot task)
          </Checkbox>
        </>
      )}
      {!qualify &&
        first.some((f) => (LEAD_QUALIFICATION_FIELDS as readonly string[]).includes(f)) && (
          <p className="note" style={{ margin: 0 }}>
            Product fit and qualification are completed by a person allowed to qualify leads.
          </p>
        )}
      {failed && <Notice kind="error">{failed}</Notice>}
    </Dialog>
  );
}

/**
 * To do by keyboard for a contact: its missing required fields and the scan
 * fields to check (prefilled with what the scan read).
 */
export function CompleteContact({ contact, onDone }: { contact: LocalRecord; onDone(): void }) {
  const { save } = useOffline();
  const toast = useToast();
  const [blockers] = useState(() => contactPushBlockers(contact));
  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(blockers.map((b) => [b.field, str(contact[b.field])])),
  );
  const [errors, setErrors] = useState<Partial<Record<ContactFormField, string>>>({});
  const [saving, setSaving] = useState(false);
  const name = fullName(contact) || str(contact.company) || 'the contact';

  async function submit() {
    if (saving) return;
    const merged = { ...contact, ...values };
    const result = validateContact(merged);
    const own = result.ok ? [] : result.errors.filter((e) => e.field in values);
    if (own.length > 0) {
      setErrors(Object.fromEntries(own.map((e) => [e.field, e.message])));
      return;
    }
    const fields: Record<string, unknown> = {};
    for (const [f, v] of Object.entries(values)) {
      if (v.trim() !== str(contact[f])) fields[f] = v.trim() || null;
    }
    const flagged = Array.isArray(contact.needs_verification)
      ? (contact.needs_verification as string[])
      : [];
    const still = flagged.filter((f) => !(f in values));
    if (still.length !== flagged.length) fields.needs_verification = still;
    if (Object.keys(fields).length === 0) return onDone();
    setSaving(true);
    try {
      await save({
        opId: newId(),
        entity: 'contact',
        id: contact.id,
        action: 'upsert',
        changedAt: new Date().toISOString(),
        fields,
      });
      toast('Documented. It goes to HubSpot by itself.');
      onDone();
    } catch {
      setSaving(false);
    }
  }

  return (
    <Dialog
      title={`Complete ${name}`}
      onClose={onDone}
      footer={
        <>
          <button type="button" className="btn" onClick={onDone}>
            Cancel
          </button>
          <ByVoiceInstead kind="contact" id={contact.id} />
          <button
            type="button"
            className="btn primary"
            disabled={saving}
            onClick={() => void submit()}
          >
            Save
          </button>
        </>
      }
    >
      {blockers.length === 0 && <p>Nothing missing.</p>}
      {blockers.map((b) => (
        <div key={b.field} className={b.code === 'unverified' ? 'vflag' : undefined}>
          <Field
            label={
              b.code === 'unverified' ? `Check ${FIELD_LABELS[b.field]}` : FIELD_LABELS[b.field]
            }
            hint={b.code === 'unverified' ? 'The scan was not sure' : undefined}
            type={b.field === 'email' ? 'email' : 'text'}
            value={values[b.field] ?? ''}
            error={errors[b.field]}
            onChange={(e) => {
              setValues((v) => ({ ...v, [b.field]: e.target.value }));
              setErrors({});
            }}
          />
        </div>
      ))}
    </Dialog>
  );
}

/** The keyboard form's way to the review call (prototype: By voice instead). */
function ByVoiceInstead({ kind, id }: { kind: 'lead' | 'contact'; id: string }) {
  const { can } = useSignedIn();
  const navigate = useNavigate();
  const location = useLocation();
  if (!can('todo.voice')) return null;
  const from = location.pathname.startsWith('/todo')
    ? '/todo'
    : `/people/${kind === 'lead' ? 'leads' : 'contacts'}/${id}`;
  return (
    <button
      type="button"
      className="btn"
      onClick={() => navigate(`/todo/voice/${kind}/${id}`, { replace: true, state: { from } })}
    >
      <Icon name="mic" />
      By voice instead
    </button>
  );
}
